/**
 * Block tree helpers over the page LoroDoc (spec §7.2–§7.4). Creating a block initialises the
 * common keys and the type-specific body via the block registry's `initBody`.
 */
import { LoroMap, LoroText, type LoroDoc, type LoroTree, type LoroTreeNode, type TreeID } from "loro-crdt";
import type { BlockType } from "@kss/schema";
import { blockDef } from "./registry";

export interface FlatBlock {
  treeId: TreeID;
  depth: number;
  type: BlockType;
  parent: TreeID | null;
  index: number;
}

export function blocksTree(doc: LoroDoc): LoroTree {
  return doc.getTree("blocks");
}

/**
 * Initialise an empty page document.
 *
 * MUST only be called once the join has settled (synced, or offline/failed): on a doc whose server
 * state has not arrived yet, the empty `title` container written here is the newer write for
 * `meta.title` and destroys the real title when the two merge.
 *
 * `registeredTitle` is the title given when the page was created, which until now lived only in
 * `docs_page`; copying it in makes the document the source of truth (I1), so the projector cannot
 * overwrite the registry with an empty string.
 */
export function ensurePageInit(doc: LoroDoc, userId: string, registeredTitle = ""): void {
  const meta = doc.getMap("meta");
  if (meta.get("schemaVersion") == null) {
    meta.set("schemaVersion", 1);
    meta.setContainer("title", new LoroText());
    meta.set("createdAt", Date.now());
    meta.set("createdBy", userId);
    doc.commit({ origin: "sys:init" });
  }
  const title = pageTitleText(doc);
  if (registeredTitle && title && title.toString() === "") {
    title.update(registeredTitle);
    doc.commit({ origin: "sys:init" });
  }
  const tree = blocksTree(doc);
  tree.enableFractionalIndex(0);
}

export function flattenBlocks(doc: LoroDoc): FlatBlock[] {
  const tree = blocksTree(doc);
  const out: FlatBlock[] = [];
  const walk = (nodes: LoroTreeNode[] | undefined, depth: number, parent: TreeID | null) => {
    if (!nodes) return;
    nodes.forEach((n, index) => {
      const type = (n.data.get("type") as BlockType | undefined) ?? "section";
      out.push({ treeId: n.id, depth, type, parent, index });
      walk(n.children(), depth + 1, n.id);
    });
  };
  walk(tree.roots(), 0, null);
  return out;
}

export function createBlock(doc: LoroDoc, type: BlockType, userId: string, parent?: TreeID, index?: number, init?: unknown): TreeID {
  const tree = blocksTree(doc);
  const node = tree.createNode(parent, index);
  const data = node.data;
  data.set("type", type);
  data.set("createdAt", Date.now());
  data.set("createdBy", userId);
  const body = data.setContainer("body", new LoroMap());
  data.setContainer("links", new LoroMap());
  data.setContainer("annotations", new LoroMap());
  blockDef(type).initBody(body, init);
  doc.commit();
  return node.id;
}

export function deleteBlock(doc: LoroDoc, treeId: TreeID): void {
  blocksTree(doc).delete(treeId);
  doc.commit();
}

export function moveBlock(doc: LoroDoc, treeId: TreeID, parent: TreeID | undefined, index: number): void {
  blocksTree(doc).move(treeId, parent, index);
  doc.commit();
}

/** Move a block directly before/after a sibling (native tree move, identity preserved). */
export function moveBlockRelative(doc: LoroDoc, treeId: TreeID, target: TreeID, where: "before" | "after"): void {
  const tree = blocksTree(doc);
  const node = tree.getNodeByID(treeId);
  const ref = tree.getNodeByID(target);
  if (!node || !ref || treeId === target) return;
  if (where === "before") node.moveBefore(ref);
  else node.moveAfter(ref);
  doc.commit();
}

export function pageTitleText(doc: LoroDoc): LoroText | null {
  const t = doc.getMap("meta").get("title");
  return t instanceof LoroText ? t : null;
}
